// When the Daily reminder fires and what it says. Pure, so it can be tested
// without a phone; lib/dailyReminder.ts does the scheduling.

import { dailyKey } from '../../../shared/src/modes';

/** Notification ids owned by the reminder. Only these are ever cancelled. */
export const REMINDER_IDS = [7101, 7102] as const;

/** The reminder hour is the one the player said yes at, kept inside the day. */
export const FIRST_HOUR = 9;
export const LAST_HOUR = 21;

/** A reminder this close to the player's last visit says nothing new: they
 *  were just here and saw the Daily card. */
export const QUIET_AFTER_VISIT_MS = 90 * 60 * 1000;

export function clampHour(hour: number): number {
  return Math.min(LAST_HOUR, Math.max(FIRST_HOUR, Math.floor(hour)));
}

/** The Daily record the store keeps in localStorage (wot_daily_v1). */
export interface DailyRecord {
  key?: string;
  streak?: number;
}

export interface PlannedReminder {
  id: number;
  at: Date;
  title: string;
  body: string;
}

/**
 * The next reminders from `now`, at most one per day and at most
 * REMINDER_IDS.length in all, so a player who stops opening the app hears
 * twice more and then nothing. A day whose Daily is already played is
 * skipped. `now` is the moment the app was last in use: the plan is rebuilt
 * on every open and after every Daily.
 */
export function planReminders(now: Date, hour: number, record: DailyRecord | null): PlannedReminder[] {
  const playedKey = record?.key ?? null;
  const streak = record?.streak ?? 0;
  const out: PlannedReminder[] = [];
  for (let day = 0; day < 4 && out.length < REMINDER_IDS.length; day++) {
    const at = new Date(now.getFullYear(), now.getMonth(), now.getDate() + day, clampHour(hour), 0, 0, 0);
    if (at.getTime() - now.getTime() < QUIET_AFTER_VISIT_MS) continue;
    // Daily sets turn over at midnight UTC; the key of the moment the
    // reminder fires names the set it points at.
    if (dailyKey(at) === playedKey) continue;
    // The streak is consecutive played days, so it is still alive on `at`
    // only if the day before was played.
    const keptAlive = streak >= 2 && playedKey === dailyKey(new Date(at.getTime() - 86_400_000));
    out.push({
      id: REMINDER_IDS[out.length],
      at,
      title: "Today's Daily is ready",
      body: keptAlive
        ? `Ten new questions. Keep your ${streak}-day streak going.`
        : 'Ten new questions, the same ten for everyone today.',
    });
  }
  return out;
}
